import JSZip from 'jszip';
import { API_BASE_URL } from '../const/constants.js';

/**
 * 将后端返回的「PDF 转页图片压缩包」解析为逐页图片。
 *
 * SubmitQuestionSplitJob 提交响应里的 ImageZipUrl 是 PDF 转成图片后的压缩包地址，
 * 前端下载解压后即可得到每一页的图片，配合 DescribeQuestionSplitJob 返回的
 * QuestionInfo[i].CoordPageIndex 就能把题目坐标框画到对应页上。
 *
 * @param {string} zipUrl - 压缩包地址
 * @returns {Promise<Array<{ ImageUrl: string, fromPdfZip: boolean }>>} 按页序排列的图片
 */
export const resolvePdfPageImages = async (zipUrl) => {
  if (!zipUrl) return [];
  try {
    const response = await fetch(zipUrl);
    if (!response.ok) {
      console.warn('[PDF] 分页图压缩包下载失败:', response.status);
      return [];
    }
    const buffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(buffer);

    const entries = Object.values(zip.files).filter(
      (file) => !file.dir && /\.(png|jpe?g|bmp)$/i.test(file.name),
    );
    // 按文件名做自然序排序，避免 page10 排在 page2 前面
    entries.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );

    const pages = [];
    for (const entry of entries) {
      /* eslint-disable no-await-in-loop */
      const blob = await entry.async('blob');
      pages.push({
        ImageUrl: URL.createObjectURL(blob),
        fromPdfZip: true,
      });
    }
    return pages;
  } catch (error) {
    console.warn('[PDF] 分页图压缩包解析失败:', error);
    return [];
  }
};

export default resolvePdfPageImages;

// ============ BFF 转存版本（推荐入口） ============

/**
 * BFF 侧下载 + 转存到我们自己 COS 的路由。
 *
 * 与多模态文档解析保持一致（DocParseRenderer 用的是
 * `demo-common/multimodalDocParse/parseResult`）：SubmitQuestionSplitJob 返回的
 * ImageZipUrl 落在别的业务 COS（预签名、非本域名），前端直连既跨域、又依赖对方
 * 签名有效期，因此改为由 BFF 下载并转存到我们自己的 COS 后再透出。
 *
 * 约定（按后端实际实现调整）：
 *   请求 POST { API_BASE_URL }demo-common/questionSplit/parseResult
 *   入参 { resultUrl: string }
 *   出参 { code: 0, data: { pages?: Array<string|{ImageUrl}> , zipUrl?: string } }
 *     - data.pages：BFF 已解压并转存好的逐页图片地址（优先）
 *     - data.zipUrl：BFF 仅把压缩包转存到我们自己的 COS，前端再下载解压
 */
// 线上 BFF 目前只有 5 条 demo-* 路由（getDemoResultByDefaultImage / getProductConfig /
// multimodalDocParse/parseResult / capi/getResult / getPresignedUrl），没有切题相关路由，
// 所以默认置空跳过探测；后端补齐后把真实路径填进来即可自动启用。
export const QUESTION_SPLIT_PAGES_PATH = '';

const normalizeBffPages = (data) => {
  const list = data?.pages || data?.imageUrls || data?.images || data?.urls;
  if (!Array.isArray(list)) return [];
  return list
    .map((item) =>
      typeof item === 'string'
        ? { ImageUrl: item, fromPdfZip: true }
        : {
            ...item,
            ImageUrl: item?.ImageUrl || item?.url || item?.Url,
            fromPdfZip: true,
          },
    )
    .filter((page) => !!page.ImageUrl);
};

/**
 * 取 PDF 逐页图片：只走 BFF（下载 + 转存到我们自己的 COS）。
 *
 * 不再回退为直连 ImageZipUrl：对方 COS 未配置 CORS，浏览器直连必然
 * `TypeError: Failed to fetch`，只会产生噪音日志。
 *
 * @param {string} zipUrl - 提交响应里的 ImageZipUrl
 * @returns {Promise<Array<{ ImageUrl: string, fromPdfZip: boolean }>>} 失败时返回 []
 */
export const resolvePdfPageImagesWithBff = async (zipUrl) => {
  if (!zipUrl) return [];

  // 路由未就绪时置空 QUESTION_SPLIT_PAGES_PATH 即跳过 BFF 探测，直接返回空
  if (!QUESTION_SPLIT_PAGES_PATH) {
    console.warn('[PDF] 未配置 QUESTION_SPLIT_PAGES_PATH，跳过分页图解析');
    return [];
  }

  try {
    const res = await fetch(`${API_BASE_URL}${QUESTION_SPLIT_PAGES_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ resultUrl: zipUrl }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data?.code === 0 && data?.data) {
        // 1) BFF 已解压并转存好逐页图片
        const pages = normalizeBffPages(data.data);
        if (pages.length > 0) return pages;

        // 2) BFF 只转存了压缩包 → 从我们自己的 COS 下载解压
        const transferredZipUrl = data.data.zipUrl || data.data.url;
        if (transferredZipUrl) {
          const localPages = await resolvePdfPageImages(transferredZipUrl);
          if (localPages.length > 0) return localPages;
        }
      }
    } else {
      console.warn(
        '[PDF] BFF 转存接口不可用:',
        res.status,
        QUESTION_SPLIT_PAGES_PATH,
      );
    }
  } catch (error) {
    console.warn('[PDF] BFF 转存接口异常:', error);
  }

  return [];
};
